import React, { ChangeEvent, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useFormContext } from "react-hook-form";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

interface CustomTextInputProps {
  name: string;
  type?: string;
  width?: string;
  height?: string;
  placeholder?: string;
  readOnly?: boolean;
  hidden?: boolean;
  mode?:
    | "none"
    | "text"
    | "decimal"
    | "numeric"
    | "tel"
    | "search"
    | "email"
    | "url";
  background?: string;
  success?: boolean;
  message?: string;
  isPasswordField?: boolean;
  isLoginPage?: boolean;
  defaultValue?: string;
  pattern?: string;
  max?: number;
  onChange?: (event: any) => void;
  isNumber?: boolean;
  fontSize?: string;
  onInput?: (event: any) => void;
}

interface MessageWrapperProps {
  type: "error" | "success" | "default";
}

const CustomTextInput = ({
  name,
  type = "text",
  height = "5.2rem",
  width = "100%",
  placeholder = "",
  readOnly = false,
  hidden,
  mode = "text",
  background = "",
  success,
  message,
  isPasswordField = false,
  isLoginPage = false,
  pattern,
  max,
  onChange,
  isNumber,
  fontSize,
  onInput,
}: CustomTextInputProps) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const { t } = useTranslation();

  const theme = useTheme();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const isPassword = name.toLowerCase().includes("password");
  const error = errors[name];

  // 메시지를 표시할지 결정하는 함수
  const shouldShowMessage = () => {
    // 로그인 페이지에서는 메시지 표시 안 함
    if (isLoginPage) {
      return false;
    }

    // 패스워드 필드(비밀번호 찾기 등)는 항상 표시
    if (isPasswordField) {
      return true;
    }

    // 나머지는 에러나 성공 상태일 때만
    return !!(error || success);
  };

  // 메시지 타입 결정
  const getMessageType = () => {
    if (error) return "error";
    if (success) return "success";
    return "default";
  };

  // 표시할 메시지 결정
  const getMessage = (): string => {
    if (isPasswordField) {
      return t(message || ""); // 패스워드 필드는 항상 기본 메시지
    }
    // 그 외에는 에러나 성공 메시지
    return error ? t(String(error.message || "")) : t(message || "") || "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
    setValue(name, e.target.value);
  };

  return (
    <InputContainer>
      <InputWrapper>
        <Input
          {...register(name)}
          type={(isPassword && passwordVisible) || isNumber ? "text" : type}
          height={height}
          width={width}
          placeholder={t(placeholder)}
          error={!!error}
          readOnly={readOnly}
          hidden={hidden}
          inputMode={mode}
          background={background}
          isLoginPage={isLoginPage}
          pattern={pattern}
          maxLength={max}
          // onChange={handleChange}
          onInput={onInput}
          fontSize={fontSize}
        />

        {isPassword && (
          <EyeIcon onClick={() => setPasswordVisible(!passwordVisible)}>
            {passwordVisible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
          </EyeIcon>
        )}
      </InputWrapper>
      {shouldShowMessage() && (
        <MessageWrapper type={getMessageType()}>
          <span>{getMessage()}</span>
          {(error?.message || success || isPasswordField) && (
            <Image
              src={
                error?.message || error
                  ? theme.image.check_orange.default
                  : success
                    ? theme.image.check_green.default
                    : theme.image.check_gray.default // isPasswordField면 gray 체크
              }
              size="1.6rem"
            />
          )}
        </MessageWrapper>
      )}
    </InputContainer>
  );
};

export default CustomTextInput;

const InputContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
  width: "100%",
});

const InputWrapper = styled.div({
  position: "relative",
  width: "100%",
});

const Input = styled.input<{
  error: boolean;
  background?: string;
  isLoginPage?: boolean;
  fontSize?: string;
}>(({ theme, width, height, error, background, isLoginPage, fontSize }) => ({
  width: width,
  height: height,
  border: error
    ? `1px solid ${theme.color.orange}`
    : `1px solid ${theme.color.light_gray}`,
  padding: "1.6rem",
  borderRadius: "0",
  background: background || "transparent",
  color: isLoginPage ? theme.color.white : theme.color.black,
  fontWeight: "400",
  fontFamily: "Pretendard",

  fontSize: fontSize ? "1.6rem" : fontSize,

  "&::placeholder": {
    color: theme.color.light_gray,
    fontWeight: theme.fontWeight.regular,
    fontSize: "1.6rem",
  },
}));

const MessageWrapper = styled.div<MessageWrapperProps>(({ theme, type }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  fontSize: "1.2rem",

  "& > span": {
    color:
      type === "error"
        ? theme.color.orange
        : type === "success"
          ? theme.color.green
          : theme.color.light_gray,
  },
}));

const EyeIcon = styled.div({
  position: "absolute",
  right: "1rem",
  top: "50%",
  transform: "translateY(-50%)",
  cursor: "pointer",
});
